import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import classNames from 'classnames';
import { useState } from 'react';
import type { GroupBase, GroupProps, SingleValue } from 'react-select';

import { useGetLoggedInUser } from '@revelio/auth';
import { FeatureFlag, isSavedQueryVariableMetric } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import { Callout, Select as RevelioSelect, Text } from '@revelio/ui';

import { useSavedQueries } from '../../api/use-saved-queries';
import {
  type AccessAnnotatedMetricOption,
  annotateMetricGroupsByAccess,
} from '../DataProvider/utils/chart-data-source-access';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  groupedMetricOptions,
  type MetricOption,
  type MetricValues,
} from '../PageConfigProvider/types/metric';
import styles from './metric-selector.module.css';
import labelStyles from './section-label.module.css';
import { applySavedQuerySelectionToItem } from './utils/apply-saved-query-selection';
import { getAvailableMetrics } from './utils/metric-mapping';
import {
  filterSavedQueriesForChart,
  findSavedQueryByMetricValue,
  savedQueryMetricValue,
} from './utils/saved-query-for-chart';

type MetricSelectorProps = {
  item: PositionedItem;
  isDisabled?: boolean;
  isReport?: boolean;
};

export const MetricSelector = ({
  item,
  isDisabled = false,
  isReport = false,
}: MetricSelectorProps) => {
  const { config, parentId, dispatch } = useTabContentConfig();
  const reportParameters = config.reportParameters ?? [];
  const customReportsSnowflakeEnabled = useUnleashFlag(
    FeatureFlag.CustomReportsSnowflake
  );
  const { data: savedQueries = [] } = useSavedQueries({
    enabled: isReport && customReportsSnowflakeEnabled,
  });

  const { loggedInUser } = useGetLoggedInUser();
  const userTabs = loggedInUser.tabs ?? [];

  const primaryFilterId = config.primaryFilter?.id;
  const chartType = item?.plot?.chartType;
  const secondaryDimension = item?.plot?.secondaryDimension;

  const compatibleCustomQueries = (() => {
    if (
      !isReport ||
      !customReportsSnowflakeEnabled ||
      chartType === undefined ||
      chartType === null
    ) {
      return [];
    }
    return filterSavedQueriesForChart({
      queries: savedQueries,
      chartType,
      parentId,
    });
  })();

  const availableMetricOptions = (() => {
    const baseMetricGroups = annotateMetricGroupsByAccess(
      getAvailableMetrics({
        primaryFilterId,
        chartType,
      }),
      { chartType, secondaryDimension, userTabs }
    );
    if (compatibleCustomQueries.length > 0) {
      baseMetricGroups.push({
        label: 'Custom variables',
        options: compatibleCustomQueries.map((q) => ({
          value: savedQueryMetricValue(q) as MetricValues,
          label: q.name,
        })),
      });
    }
    return baseMetricGroups;
  })();

  const id = item?.id;

  const { defaultMetricOptions } = item?.plot ?? { defaultMetricOptions: [] };
  const dataSource = item?.plot?.dataSource;
  const savedQueryId = item?.plot?.snowflakeConfig?.savedQueryId;

  const resolvedPlotMetric: MetricValues | undefined = (() => {
    if (
      defaultMetricOptions?.length === 1 &&
      defaultMetricOptions?.[0] === 'share_of_headcount'
    ) {
      return 'headcount';
    }
    if (
      dataSource === 'snowflake' &&
      savedQueryId !== undefined &&
      savedQueryId !== ''
    ) {
      const fromDto = savedQueries.find((q) => q.id === savedQueryId);
      if (fromDto) {
        return savedQueryMetricValue(fromDto) as MetricValues;
      }
      return `sq:${savedQueryId}` as MetricValues;
    }
    return defaultMetricOptions?.[0];
  })();

  const flatAvailableOptions = availableMetricOptions.flatMap(
    (group) => group.options
  );

  const matchedStandardOption = (() => {
    if (resolvedPlotMetric === undefined) return undefined;
    return flatAvailableOptions.find((opt) => opt.value === resolvedPlotMetric);
  })();

  const metricSelectOptions = (() => {
    if (
      primaryFilterId === undefined ||
      matchedStandardOption ||
      resolvedPlotMetric === undefined ||
      resolvedPlotMetric === null
    ) {
      return availableMetricOptions;
    }
    const orphanLabel =
      groupedMetricOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === resolvedPlotMetric)?.label ??
      String(resolvedPlotMetric);
    return [
      {
        label: 'Current selection',
        options: [{ value: resolvedPlotMetric, label: orphanLabel }],
      },
      ...availableMetricOptions,
    ];
  })();

  const selectedMetric: MetricOption | null = (() => {
    if (
      defaultMetricOptions?.length === 1 &&
      defaultMetricOptions?.[0] === 'share_of_headcount'
    ) {
      return {
        value: 'headcount',
        label: 'Headcount',
      };
    }

    if (resolvedPlotMetric === undefined || resolvedPlotMetric === null) {
      return metricSelectOptions[0]?.options[0] ?? null;
    }

    const allForSelect = metricSelectOptions.flatMap((group) => group.options);
    const selected = allForSelect.find(
      (opt) => opt.value === resolvedPlotMetric
    );

    return selected ?? metricSelectOptions[0]?.options[0] ?? null;
  })();

  const showOrphanMetricWarning =
    primaryFilterId !== undefined &&
    resolvedPlotMetric !== undefined &&
    resolvedPlotMetric !== null &&
    matchedStandardOption === undefined;

  const handleChangeMetric = (
    metric: SingleValue<AccessAnnotatedMetricOption>
  ) => {
    if (!id) return;
    const next = metric?.value;
    if (next === undefined) {
      dispatch({
        type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
        payload: { id, defaultMetricOptions: [] },
      });
      return;
    }

    if (isSavedQueryVariableMetric(next)) {
      const query = findSavedQueryByMetricValue(savedQueries, next);
      if (!query) return;
      dispatch({
        type: 'UPDATE_ITEM_DATASOURCE',
        payload: { id, dataSource: 'snowflake' },
      });
      applySavedQuerySelectionToItem({
        itemId: id,
        query,
        reportParameters,
        dispatch,
      });
      dispatch({
        type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
        payload: { id, defaultMetricOptions: [next] },
      });
      return;
    }

    dispatch({
      type: 'UPDATE_ITEM_DATASOURCE',
      payload: { id, dataSource: 'mw-gql' },
    });
    dispatch({
      type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
      payload: { id, defaultMetricOptions: [next] },
    });
  };

  const CollapsibleGroup = (
    props: GroupProps<
      AccessAnnotatedMetricOption,
      false,
      GroupBase<AccessAnnotatedMetricOption>
    >
  ) => {
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const { data, children, innerProps } = props;

    const label = String(data.label);

    const isOpen = openGroups[label] ?? true;

    const toggle = () =>
      setOpenGroups((prev) => ({ ...prev, [label]: !(prev[label] ?? true) }));

    return (
      <div {...innerProps}>
        <div className={styles.groupHeader} onClick={toggle}>
          <Text fontSize="sm" weight="semibold" className={styles.groupLabel}>
            {label}
          </Text>
          <IconChevronDownSmall
            size={16}
            className={styles.groupChevron}
            data-open={isOpen || undefined}
          />
        </div>

        {isOpen && <div className={styles.groupBody}>{children}</div>}
      </div>
    );
  };

  if (
    (!metricSelectOptions || metricSelectOptions.length === 0) &&
    !showOrphanMetricWarning
  ) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Text
          className={classNames(labelStyles.sectionLabel, styles.variableLabel)}
          fontSize="sm"
          data-disabled={isDisabled || undefined}
        >
          Variable
        </Text>
      </div>
      {showOrphanMetricWarning && (
        <Callout
          variant="warning"
          className={styles.orphanWarning}
          data-testid="metric-selector-unsupported-warning"
        >
          This chart&apos;s saved variable isn&apos;t available for the current
          chart type and filters. Choose a variable below or change the chart
          type.
        </Callout>
      )}
      <div className={styles.selectRow}>
        <RevelioSelect<AccessAnnotatedMetricOption>
          name="metric-selector"
          options={metricSelectOptions}
          value={selectedMetric}
          onChange={handleChangeMetric}
          isSearchable
          isDisabled={isDisabled}
          isOptionDisabled={(option) => Boolean(option.isDisabled)}
          size="sm"
          components={{
            Group: CollapsibleGroup,
          }}
        />
      </div>
    </div>
  );
};
